import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Switch, Divider, Avatar,
  CircularProgress, alpha, useTheme, useMediaQuery,
} from '@mui/material';
import { MergeType as MergeIcon } from '@mui/icons-material';
import { format } from 'date-fns';

const MERGE_FIELDS = [
  { key: 'name', label: 'Nome' },
  { key: 'phone', label: 'Telefone' },
  { key: 'email', label: 'Email' },
  { key: 'cpf', label: 'CPF' },
  { key: 'birthDate', label: 'Data de Nascimento', format: (v) => v ? format(new Date(v), 'dd/MM/yyyy') : null },
  { key: 'gender', label: 'Gênero' },
  { key: 'addressStreet', label: 'Rua' },
  { key: 'addressNumber', label: 'Número' },
  { key: 'addressComplement', label: 'Complemento' },
  { key: 'addressNeighborhood', label: 'Bairro' },
  { key: 'addressCity', label: 'Cidade' },
  { key: 'addressState', label: 'Estado' },
  { key: 'addressZipCode', label: 'CEP' },
  { key: 'addressCountry', label: 'País' },
  { key: 'notes', label: 'Observações' },
];

function formatValue(field, value) {
  if (value == null || value === '') return '—';
  if (field.format) return field.format(value) || '—';
  return value;
}

function FieldRow({ field, personA, personB, selected, onToggle }) {
  const theme = useTheme();
  const valA = personA[field.key];
  const valB = personB[field.key];
  const displayA = formatValue(field, valA);
  const displayB = formatValue(field, valB);

  const bothEmpty = (!valA && !valB) || (valA === '' && valB === '');
  const identical = valA === valB;
  const onlyA = valA && !valB;
  const onlyB = !valA && valB;
  const disabled = bothEmpty || identical || onlyA || onlyB;

  const isA = selected === 'a';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: 1, '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) } }}>
      {/* Valor A */}
      <Box sx={{ flex: 1, textAlign: 'right', pr: 2 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isA ? 600 : 400,
            color: isA ? 'text.primary' : 'text.disabled',
            transition: 'all 0.2s',
          }}
        >
          {displayA}
        </Typography>
      </Box>

      {/* Switch central */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mb: 0.25 }}>
          {field.label}
        </Typography>
        <Switch
          size="small"
          checked={!isA}
          onChange={() => onToggle(isA ? 'b' : 'a')}
          disabled={disabled}
          color="primary"
        />
      </Box>

      {/* Valor B */}
      <Box sx={{ flex: 1, pl: 2 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: !isA ? 600 : 400,
            color: !isA ? 'text.primary' : 'text.disabled',
            transition: 'all 0.2s',
          }}
        >
          {displayB}
        </Typography>
      </Box>
    </Box>
  );
}

export default function PersonMergeDialog({ open, onClose, pair, onMerge, isMerging }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [selections, setSelections] = useState({});

  // Inicializa seleções ao abrir
  useEffect(() => {
    if (!pair) return;
    const initial = {};
    for (const field of MERGE_FIELDS) {
      const valA = pair.personA[field.key];
      const valB = pair.personB[field.key];
      if (!valA && valB) {
        initial[field.key] = 'b';
      } else {
        initial[field.key] = 'a';
      }
    }
    // photoId tratado separadamente
    if (!pair.personA.photoUrl && pair.personB.photoUrl) {
      initial.photoId = 'b';
    } else {
      initial.photoId = 'a';
    }
    setSelections(initial);
  }, [pair]);

  const handleToggle = (key, value) => {
    setSelections((prev) => ({ ...prev, [key]: value }));
  };

  const handleMerge = () => {
    if (!pair) return;
    const a = pair.personA;
    const b = pair.personB;

    const preferredData = {};
    for (const field of MERGE_FIELDS) {
      const source = selections[field.key] === 'b' ? b : a;
      preferredData[field.key] = source[field.key] ?? null;
    }
    // photoId
    preferredData.photoId = selections.photoId === 'b' ? b.photoId : a.photoId;

    onMerge({
      keepPersonId: a.id,
      removePersonId: b.id,
      preferredData,
    });
  };

  if (!pair) return null;

  const { personA, personB } = pair;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeIcon color="primary" />
        Unificar Pessoas
      </DialogTitle>

      <DialogContent dividers>
        {/* Header com fotos e nomes */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', mb: 2, py: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              src={personA.photoUrl}
              sx={{
                width: 56, height: 56, mx: 'auto', mb: 0.5,
                border: `2px solid ${alpha(theme.palette.success.main, 0.5)}`,
              }}
            >
              {personA.name?.[0]}
            </Avatar>
            <Typography variant="subtitle2" fontWeight="bold">{personA.name}</Typography>
            <Typography variant="caption" color="text.secondary">Mais antigo</Typography>
          </Box>

          <Typography variant="h6" color="text.disabled" sx={{ mx: 2 }}>VS</Typography>

          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              src={personB.photoUrl}
              sx={{
                width: 56, height: 56, mx: 'auto', mb: 0.5,
                border: `2px solid ${alpha(theme.palette.warning.main, 0.5)}`,
              }}
            >
              {personB.name?.[0]}
            </Avatar>
            <Typography variant="subtitle2" fontWeight="bold">{personB.name}</Typography>
            <Typography variant="caption" color="text.secondary">Mais recente</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Linhas de campos */}
        {MERGE_FIELDS.map((field) => (
          <FieldRow
            key={field.key}
            field={field}
            personA={personA}
            personB={personB}
            selected={selections[field.key] || 'a'}
            onToggle={(val) => handleToggle(field.key, val)}
          />
        ))}

        {/* Foto (se alguma tiver) */}
        {(personA.photoUrl || personB.photoUrl) && (
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: 1 }}>
            <Box sx={{ flex: 1, textAlign: 'right', pr: 2 }}>
              {personA.photoUrl ? (
                <Avatar src={personA.photoUrl} sx={{ ml: 'auto', width: 40, height: 40, opacity: selections.photoId === 'a' ? 1 : 0.3 }} />
              ) : (
                <Typography variant="body2" color="text.disabled">—</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mb: 0.25 }}>Foto</Typography>
              <Switch
                size="small"
                checked={selections.photoId !== 'a'}
                onChange={() => handleToggle('photoId', selections.photoId === 'a' ? 'b' : 'a')}
                disabled={!personA.photoUrl || !personB.photoUrl}
                color="primary"
              />
            </Box>
            <Box sx={{ flex: 1, pl: 2 }}>
              {personB.photoUrl ? (
                <Avatar src={personB.photoUrl} sx={{ width: 40, height: 40, opacity: selections.photoId === 'b' ? 1 : 0.3 }} />
              ) : (
                <Typography variant="body2" color="text.disabled">—</Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isMerging}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleMerge}
          disabled={isMerging}
          startIcon={isMerging ? <CircularProgress size={18} /> : <MergeIcon />}
        >
          {isMerging ? 'Unificando...' : 'Unificar Pessoa'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
